import { ExperienceCta, GemSalesConfig, GeneratedExperience, GeneratedExperienceSection } from "./types";
import { buildTokenCss } from "./ui-tokens";

export function renderGeneratedExperiencePageHtml(experience: GeneratedExperience, config: GemSalesConfig): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="robots" content="noindex,nofollow" />
    <title>${escapeHtml(experience.title)} | ${escapeHtml(config.brandName)}</title>
    <style>
      :root { ${buildTokenCss(config)} }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: var(--gem-color-surface);
        color: var(--gem-color-text);
        font-family: var(--gem-font-body);
      }
      .gemsales-page {
        min-height: 100vh;
        display: grid;
        grid-template-rows: auto 1fr;
      }
      .gemsales-page-bar {
        border-bottom: 1px solid var(--gem-color-border);
        background: var(--gem-color-surface);
      }
      .gemsales-page-bar-inner {
        width: min(100% - 32px, 960px);
        margin: 0 auto;
        min-height: 56px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
      }
      .gemsales-brand {
        color: var(--gem-color-text);
        font: 800 14px/1.2 var(--gem-font-heading);
        text-decoration: none;
      }
      .gemsales-page-main {
        width: min(100% - 32px, 960px);
        margin: 0 auto;
        padding: 32px 0 48px;
      }
      .gemsales-experience {
        display: grid;
        gap: 18px;
      }
      .gemsales-experience-header {
        display: grid;
        gap: 8px;
        padding-bottom: 12px;
      }
      .gemsales-eyebrow {
        margin: 0;
        color: var(--gem-color-muted);
        font: 800 12px/1.2 var(--gem-font-body);
        text-transform: uppercase;
        letter-spacing: 0;
      }
      h2 {
        margin: 0;
        max-width: 760px;
        color: var(--gem-color-text);
        font: 800 30px/1.16 var(--gem-font-heading);
        letter-spacing: 0;
      }
      .experience {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      .experience-section {
        border: 1px solid var(--gem-color-border);
        border-radius: var(--gem-radius-md);
        background: var(--gem-color-surface);
        padding: 16px;
        box-shadow: var(--gem-shadow-sm);
      }
      .experience-section.summary,
      .experience-section.product,
      .experience-section.comparison,
      .experience-section.timeline {
        grid-column: 1 / -1;
      }
      .experience-section h4 {
        margin: 0 0 8px;
        color: var(--gem-color-text);
        font: 800 15px/1.25 var(--gem-font-heading);
        letter-spacing: 0;
      }
      .experience-section p {
        margin: 0;
        color: var(--gem-color-text);
        font: 500 14px/1.5 var(--gem-font-body);
      }
      .experience-section.proof {
        border-left: 3px solid var(--gem-color-info);
      }
      .experience-section.product {
        background: var(--gem-color-surface-alt);
      }
      .experience-section.metric {
        display: grid;
        gap: 4px;
      }
      .metric-value {
        color: var(--gem-color-primary);
        font: 800 24px/1.1 var(--gem-font-heading);
      }
      .product-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 10px 0 0;
        color: var(--gem-color-muted);
        font: 800 12px/1.2 var(--gem-font-body);
      }
      .product-link,
      .secondary {
        display: inline-flex;
        color: var(--gem-color-primary);
        font: 800 14px/1.2 var(--gem-font-body);
        text-decoration: none;
      }
      .product-link {
        margin-top: 10px;
      }
      .comparison-table {
        display: grid;
        border: 1px solid var(--gem-color-border);
        border-radius: var(--gem-radius-sm);
        overflow: hidden;
      }
      .comparison-row {
        display: grid;
        grid-template-columns: minmax(120px, 0.8fr) minmax(0, 1.8fr);
        gap: 12px;
        padding: 10px 12px;
        border-top: 1px solid var(--gem-color-border);
        font: 500 14px/1.4 var(--gem-font-body);
      }
      .comparison-row:first-child {
        border-top: 0;
      }
      .comparison-label {
        color: var(--gem-color-muted);
        font-weight: 800;
      }
      .comparison-value {
        color: var(--gem-color-text);
        font-weight: 700;
      }
      .comparison-detail {
        display: block;
        margin-top: 3px;
        color: var(--gem-color-muted);
        font-weight: 500;
      }
      .timeline-list {
        display: grid;
        gap: 10px;
      }
      .timeline-item {
        display: grid;
        gap: 4px;
        border-top: 1px solid var(--gem-color-border);
        padding-top: 10px;
      }
      .timeline-item:first-child {
        border-top: 0;
        padding-top: 0;
      }
      .timeline-label {
        color: var(--gem-color-primary);
        font: 850 13px/1.2 var(--gem-font-body);
      }
      .caution {
        border-left: 3px solid var(--gem-color-warning);
        border-radius: var(--gem-radius-md);
        background: var(--gem-color-surface-alt);
        padding: 13px 14px;
        color: var(--gem-color-text);
        font: 500 14px/1.5 var(--gem-font-body);
      }
      .primary {
        width: fit-content;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 42px;
        padding: 11px 16px;
        border-radius: var(--gem-radius-sm);
        background: var(--gem-color-primary);
        color: #fff;
        font: 800 14px/1.2 var(--gem-font-body);
        text-decoration: none;
      }
      .primary:hover {
        background: var(--gem-color-primary-hover);
      }
      @media (max-width: 720px) {
        .gemsales-page-bar-inner,
        .gemsales-page-main {
          width: min(100% - 24px, 960px);
        }
        .gemsales-page-main {
          padding: 24px 0 36px;
        }
        h2 {
          font-size: 24px;
        }
        .experience {
          grid-template-columns: 1fr;
        }
        .comparison-row {
          grid-template-columns: 1fr;
          gap: 4px;
        }
      }
    </style>
  </head>
  <body>
    <div class="gemsales-page">
      <header class="gemsales-page-bar">
        <div class="gemsales-page-bar-inner">
          <a class="gemsales-brand" href="/">${escapeHtml(config.brandName)}</a>
        </div>
      </header>
      <main class="gemsales-page-main">
        ${renderGeneratedExperienceHtml(experience, config)}
      </main>
    </div>
  </body>
</html>`;
}

export function renderGeneratedExperienceHtml(experience: GeneratedExperience, config: GemSalesConfig): string {
  return `
    <article class="gemsales-experience" data-experience-id="${escapeHtml(experience.id)}" data-template-id="${escapeHtml(experience.templateId)}">
      <header class="gemsales-experience-header">
        <p class="gemsales-eyebrow">${escapeHtml(config.brandName)} guide</p>
        <h2>${escapeHtml(experience.title)}</h2>
      </header>
      <div class="experience">
        ${renderExperienceSectionsHtml(experience, config)}
      </div>
      ${renderExperienceCtaHtml(experience.primaryCta, "primary")}
      ${renderExperienceCtaHtml(experience.secondaryCta ?? null, "secondary")}
    </article>
  `;
}

export function renderExperienceSectionsHtml(experience: GeneratedExperience, config: GemSalesConfig): string {
  return experience.sections.map((section) => renderExperienceSectionHtml(section, config)).join("");
}

export function renderExperienceSectionHtml(section: GeneratedExperienceSection, config: GemSalesConfig): string {
  if (section.type === "summary") {
    return `
      <section class="experience-section summary" data-experience-section="summary">
        <h4>${escapeHtml(section.title)}</h4>
        <p>${escapeHtml(section.body)}</p>
      </section>
    `;
  }

  if (section.type === "product") {
    const price = Number.isFinite(section.price) ? `${escapeHtml(config.commerce.currency)} ${section.price}` : null;

    return `
      <section class="experience-section product" data-experience-section="product" data-product-handle="${escapeHtml(section.productHandle)}">
        <h4>${escapeHtml(section.title)}</h4>
        <p>${escapeHtml(section.body)}</p>
        <div class="product-meta">
          ${price ? `<span>${price}</span>` : ""}
          ${section.format ? `<span>${escapeHtml(section.format)}</span>` : ""}
        </div>
        ${
          section.url && section.ctaLabel
            ? `<a class="product-link" href="${escapeHtml(section.url)}">${escapeHtml(section.ctaLabel)}</a>`
            : ""
        }
      </section>
    `;
  }

  if (section.type === "proof") {
    return `
      <section class="experience-section proof" data-experience-section="proof">
        <h4>${escapeHtml(section.title)}</h4>
        <p>${escapeHtml(section.body)}</p>
      </section>
    `;
  }

  if (section.type === "comparison") {
    return `
      <section class="experience-section comparison" data-experience-section="comparison">
        <h4>${escapeHtml(section.title)}</h4>
        <div class="comparison-table">
          ${section.rows
            .map(
              (row) => `
                <div class="comparison-row">
                  <span class="comparison-label">${escapeHtml(row.label)}</span>
                  <span class="comparison-value">
                    ${escapeHtml(row.value)}
                    ${row.detail ? `<span class="comparison-detail">${escapeHtml(row.detail)}</span>` : ""}
                  </span>
                </div>
              `,
            )
            .join("")}
        </div>
      </section>
    `;
  }

  if (section.type === "metric") {
    return `
      <section class="experience-section metric" data-experience-section="metric">
        <h4>${escapeHtml(section.label)}</h4>
        <span class="metric-value">${escapeHtml(section.value)}</span>
        ${section.detail ? `<p>${escapeHtml(section.detail)}</p>` : ""}
      </section>
    `;
  }

  if (section.type === "timeline") {
    return `
      <section class="experience-section timeline" data-experience-section="timeline">
        <h4>${escapeHtml(section.title)}</h4>
        <div class="timeline-list">
          ${section.items
            .map(
              (item) => `
                <div class="timeline-item">
                  <span class="timeline-label">${escapeHtml(item.label)}</span>
                  <p>${escapeHtml(item.body)}</p>
                </div>
              `,
            )
            .join("")}
        </div>
      </section>
    `;
  }

  return `<p class="caution" data-experience-section="caution">${escapeHtml(section.body)}</p>`;
}

function renderExperienceCtaHtml(cta: ExperienceCta | null, variant: "primary" | "secondary"): string {
  if (!cta) return "";

  return `<a class="${variant === "primary" ? "primary link" : "secondary"}" data-action="${variant === "primary" ? "cta" : "secondary-cta"}" href="${escapeHtml(cta.url)}">${escapeHtml(cta.label)}</a>`;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
